import React, { memo, useEffect, useMemo, useState } from "react";
import type { PluggableList } from "unified";
import _, { set } from "lodash";
import { marked } from "marked";
import DOMPurify from "dompurify";
import cx from 'classnames';
import styles from './chat.module.scss';
import { translations } from "../../../../i18n";
import { useDispatch } from "react-redux";
import { invalidateSeekChatAnimation, provideFeedbackForChat, seekAnswerForQuestion } from "../../../../redux/actions/seek";
import dateFormat from "dateformat";
import { Dataset } from "../../../../types/dataseek";
import { SeekChat, SeekChatState } from "../../../../types/seek";
import CodeEditor from "../codeEditor";
import { useSelector } from "react-redux";
import LineChartView from "../charts/line";
import { ChainOfThought } from "./ChainOfThought";
import BarChartView from "../charts/bar";
import ScatterChartView from "../charts/scatter";
import PieChartView from "../charts/pie";
import { MarkdownTypewriter } from "react-markdown-typewriter";
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';



function changeUrlOrigin(url: string) {
  try {
    const currentOrigin = window.location.origin;
    const parsedUrl = new URL(url);
    const updatedUrl = `${currentOrigin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    return updatedUrl;
  } catch (error) {
    console.error('Invalid URL:', error);
    return url;
  }
}

function getViewerUrl(url: string) {
  const updatedUrl = changeUrlOrigin(url);
  return updatedUrl.replace('/document/', '/viewer/');
}

function scrollChatIntoView(animate: boolean) {
  const scrollContainer = document.querySelector(`.seek-panel__body`);
  const sessionContainer = document.querySelector(`#seek_session_container`);
  if (scrollContainer && sessionContainer) {
    const chats = sessionContainer.querySelectorAll('.chat_message');
    if (chats.length <= 1) {
      return;
    }

    const chat = chats[chats.length - 1];
    //chat.scrollIntoView({ behavior: animate ? "smooth" : "instant" });
    if (!chat) {
      return;
    }

    const rect = chat.getBoundingClientRect();
    const scrollBy = rect.top - 270;
    scrollContainer.scrollBy({ top: scrollBy, behavior: animate ? "smooth" : "instant" });
  }
}

interface CopyButtonProps {
  chat: SeekChat;
  asset?: string | null;
  /** When set, used instead of chat.answer (e.g. after placeholder substitution). */
  answerForCopy?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ chat, asset, answerForCopy }) => {
  const [copied, setCopied] = React.useState(false);

  const generateAnswer = (chat: SeekChat) => {
    var div = document.createElement("div");
    div.innerHTML = answerForCopy ?? (_.get(chat, 'answer') || "");
    var answer = div.textContent || div.innerText || "";

    if (!answer) {
      return '';
    }

    const copyCitations = !asset;
    if (copyCitations) {
      const assets = _.get(chat, 'contents', []);
      if (_.size(assets) > 0) {
        const citation = assets.map((asset, index) => {
          const url = getViewerUrl(_.get(asset, 'metadata.url'));
          return `[${index + 1}] ${url}`;
        }).join('\n');

        answer = `${answer}\n\n${citation}`;
      }
    }

    return answer;
  }

  const handleCopy = () => {
    const answer = generateAnswer(chat);
    navigator.clipboard.writeText(answer)
      .then(data => {
        setCopied(true);

        window.setTimeout(() => {
          setCopied(false);

        }, 3000);
      }).catch(err => {
        setCopied(false);
      });
  }

  return (
    <button className={cx(styles.action, styles.copy)} onClick={handleCopy}>
      <img src={`${process.env.IMAGE_CDN_URL}/images/seek/copy.svg`} />{' '}
      {copied
        ? translations.translation['seek.copied']
        : translations.translation['seek.copy_response']}
    </button>
  );
}

interface SeekChatContainerProps {
  chat: SeekChatState;
  dataset: Dataset;
  answerQuestion: (question: any) => void;
}

export const SeekChatContainer = memo<SeekChatContainerProps>(function SeekChatContainer({ chat, dataset, answerQuestion }) {
  const dispatch = useDispatch();

  const handleFeedbackClicked = React.useCallback((isHelpful: boolean) => {
    // Handle feedback logic here
    dispatch(provideFeedbackForChat({
      chat_id: chat.item.id,
      dataset_resource_id: dataset.id,
      helpful: isHelpful,
      comment: ""
    }));
  }, [chat.item.id, dataset.id]);

  const handleAnimationComplete = React.useCallback(() => {
    dispatch(invalidateSeekChatAnimation(chat.uuid));
  }, [chat.uuid]);

  useEffect(() => {
    scrollChatIntoView(true);
  }, []);

  return (
    <SeekMessage
      message={chat}
      onAnimationComplete={handleAnimationComplete}
      onFeedback={handleFeedbackClicked}
    />
  );
});

interface SeekMessageProps {
  message: SeekChatState;
  onAnimationComplete?: () => void;
  onFeedback: (helpful: boolean) => void;
}

function getResourceUrl(ckanId: string): string {
  const base = `${window.location.origin}${window.location.pathname}`.replace(/\/$/, '');
  return `${base}/resource/${ckanId}`;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Escape text for use inside HTML text or double-quoted attributes. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Replace dataset collection ids with CKAN ids only inside `<a>...</a>` (opening tag attributes and inner HTML).
 * Plain text outside anchors is left unchanged so prose that mentions the same strings is not rewritten.
 */
function replaceDatasetCollectionIdsInAnchorElements(
  html: string,
  patterns: { pattern: string; ckanId: string }[]
): string {
  if (!patterns.length || !/<a\b/i.test(html)) {
    return html;
  }
  return html.replace(/<a([^>]*)>([\s\S]*?)<\/a>/gi, (_full, attrs: string, inner: string) => {
    let newAttrs = attrs;
    let newInner = inner;
    for (const { pattern, ckanId } of patterns) {
      const re = new RegExp(escapeRegExp(pattern), 'g');
      newAttrs = newAttrs.replace(re, ckanId);
      newInner = newInner.replace(re, ckanId);
    }
    return `<a${newAttrs}>${newInner}</a>`;
  });
}

/** rehype-sanitize schema: allow `target` / `rel` on links (backend HTML + dataset page link). */
const seekRehypeSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), 'target', 'rel'],
  },
};

/** Replace model placeholders: `<DATASET_PAGE_URL>` → HTML anchor; dataset collection ids → CKAN ids only within `<a>` tags. */
function replaceSeekAnswerPlaceholders(
  answer: string,
  datasets: Dataset[] | undefined,
  datasetResourceName: string | undefined | null
): string {
  if (!answer) {
    return answer;
  }
  const pageUrl = window.location.href;
  const linkLabel = (datasetResourceName && datasetResourceName.trim()) || pageUrl;
  const datasetPageAnchor = `<a href="${escapeHtml(pageUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(linkLabel)}</a>`;
  let result = answer.replace(/<DATASET_PAGE_URL>/g, datasetPageAnchor);
  if (!datasets?.length) {
    return result;
  }
  const patterns: { pattern: string; ckanId: string }[] = [];
  for (const dataset of datasets) {
    const collection = dataset.dataset_collection;
    const underscoreForm = collection.replace(/^dataset\./, 'dataset_');
    patterns.push({ pattern: collection, ckanId: dataset.ckan_id });
    if (underscoreForm !== collection) {
      patterns.push({ pattern: underscoreForm, ckanId: dataset.ckan_id });
    }
  }
  patterns.sort((a, b) => b.pattern.length - a.pattern.length);
  return replaceDatasetCollectionIdsInAnchorElements(result, patterns);
}

const SeekMessage: React.FC<SeekMessageProps> = ({ message, onAnimationComplete, onFeedback }) => {
  const [showActions, setShowActions] = React.useState(false);
  const [typing, setTyping] = React.useState(true);
  const [showPlan, setShowPlan] = React.useState(false);
  const answerRef = React.useRef<HTMLDivElement | null>(null);
  const isUserScrollingRef = React.useRef(false);
  const userScrollTimeoutRef = React.useRef<any>(null);
  const scrollBottomInterval = React.useRef<any>(null);

  const querying = useMemo(() => {
    return _.get(message, 'querying', false);
  }, [message]);

  const isAtBottomRef = React.useRef(true);

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  const rehypePlugins = useMemo(
    (): PluggableList => [rehypeRaw, [rehypeSanitize, seekRehypeSanitizeSchema]],
    []
  );

  const markdownComponents = useMemo(
    () => ({
      a: ({ node: _node, href, children, ...props }: any) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      ),
    }),
    []
  );


  const { question, answer, animate } = useMemo(() => {
    const question = _.get(message, 'item.question') || "";
    const answer = _.get(message, 'item.answer') || "";
    const animate = _.get(message, 'animate', false);

    return { question, answer, animate };
  }, [message]);

  const isNearBottom = (element: HTMLElement) => {
    const threshold = 40;
    return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
  };

  const datasetResource = useSelector((state: any) => {
    return state.dataset.item;
  });

  const datasetCitations = useMemo(() => {
    if(message?.item?.dataset_source_ids && message?.item?.dataset_source_ids?.length > 0) {
      const datasetIds = message.item.dataset_source_ids;
      const datasets = datasetResource?.datasets;
      if(datasets && datasets.length > 0) {
        return datasets.filter((dataset: Dataset) => datasetIds.includes(dataset.dataset_collection));
      }
    }

    return [];
  }, [message, datasetResource]);

  const displayAnswer = useMemo(
    () => replaceSeekAnswerPlaceholders(answer, datasetResource?.datasets, datasetResource?.name),
    [answer, datasetResource]
  );

  useEffect(() => {
    const scrollContainer = document.querySelector('.seek-panel__body');

    if (!scrollContainer) return;

    const handleScroll = () => {
      isUserScrollingRef.current = true;

      const el = scrollContainer as HTMLElement;
      isAtBottomRef.current = isNearBottom(el);

      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }

      userScrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = !isAtBottomRef.current;
      }, 300);
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);


  const scrollToBottom = (animate: boolean) => {
    const scrollContainer = document.querySelector(`.seek-panel__body`);
    const sessionContainer = document.querySelector(`#seek_session_container`);
    if (scrollContainer && sessionContainer) {
      scrollContainer.scrollTo({ top: sessionContainer.scrollHeight, behavior: animate ? "smooth" : "instant" });
    }
  }

  const scrollToBottomWhileTyping = () => {
    clearScrollBottomInterval();
    scrollBottomInterval.current = window.setInterval(() => {
      if (typing && !isUserScrollingRef.current && isAtBottomRef.current) {
        scrollToBottom(false);
      }
    }, 100);
  }

  const clearScrollBottomInterval = () => {
    if (scrollBottomInterval.current) {
      window.clearInterval(scrollBottomInterval.current);
      scrollBottomInterval.current = null;
    }
  }

  const handleAnimationComplete = () => {
    console.log("Animation complete");

    setTyping(false);
    setShowActions(true);

    clearScrollBottomInterval();

    setTimeout(() => {
      if (isAtBottomRef.current) {
        scrollChatIntoView(true);
      }
    }, 1000);

    if (onAnimationComplete) {
      onAnimationComplete();
    }
  }

  const handleFeedbackClicked = (isHelpful: boolean) => {
    onFeedback(isHelpful);
  }

  const sanitizedAnswer = (answer: string) => {
    let processedAnswer = answer
      .replace(/<\/p>/g, '\n\n')
      .replace(/<p>/g, '')
      .replace(/\n{3,}/g, '\n\n');

    let markedAnswer = marked(processedAnswer) as string;

    let sanitizedAnswer = DOMPurify.sanitize(markedAnswer, {
      ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td']
    });

    return sanitizedAnswer;
  }

  useEffect(() => {
    scrollToBottomWhileTyping();
    return () => {
      clearScrollBottomInterval();
      onAnimationComplete?.();
    }
  }, []);

  return (
    <div className={cx(styles.chat, 'chat_message')}>
      <div className={styles.question}>
        <p className={cx(styles.text)}>{question}</p>
      </div>
      <div className={styles.answer}>
        <ChainOfThought
          querying={querying}
          events={_.get(message, 'events', [])}
          defaultExpanded={false}
        />
        <div className={cx(styles.answerContent, styles.markdown)}>
          <Markdown
            remarkPlugins={remarkPlugins}
            rehypePlugins={rehypePlugins}
            components={markdownComponents}
          >
            {displayAnswer}
          </Markdown>
        </div>
      </div>

      {!querying && message.item.artifacts && <GraphView chat={message.item} />}

      {!querying && datasetCitations.length > 0 && (
        <div className={styles.datasetSources}>
          <div className={styles.datasetSourcesHeading}>
            {translations.translation['seek.answer_derived_from_resources']}
          </div>
          <ul className={styles.datasetSourcesList}>
            {datasetCitations.map((dataset: Dataset) => {
              const href = getResourceUrl(dataset.ckan_id);
              return (
                <li key={dataset.id}>
                  <a href={href} target="_blank" rel="noopener noreferrer" className={styles.datasetSourceLink}>
                    {dataset.name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className={cx(styles.actions, {
        [styles.show]: !querying
      })}>
        <CopyButton chat={message.item} answerForCopy={displayAnswer} />

        <div className={styles.feedbacks}>
          <button className={cx(styles.action, styles.thumbsUp)} onClick={() => handleFeedbackClicked(true)}>
            <img src={message.item.isHelpful == true ? `${process.env.IMAGE_CDN_URL}/images/seek/thumbs_filled.svg` : `${process.env.IMAGE_CDN_URL}/images/seek/thumbs.svg`} />
          </button>
          <button className={cx(styles.action, styles.thumbsDown)} onClick={() => handleFeedbackClicked(false)}>
            <img src={message.item.isHelpful == false ? `${process.env.IMAGE_CDN_URL}/images/seek/thumbs_filled.svg` : `${process.env.IMAGE_CDN_URL}/images/seek/thumbs.svg`} />
          </button>
        </div>
      </div>
    </div>
  );
}

const GraphView = memo<{ chat: SeekChat }>(function GraphView({ chat }) {
  const artifacts = chat.artifacts;

  if (
    !artifacts ||
    !_.get(artifacts, "series") ||
    _.get(artifacts, "series", []).length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.graphView}>
      <div className={styles.secondaryContainer}>
        {_.get(artifacts, "meta.type") === "line" && (
          <LineChartView artifacts={artifacts} />
        )}

        {_.get(artifacts, "meta.type") === "bar" && (
          <BarChartView artifacts={artifacts} />
        )}

        {_.get(artifacts, "meta.type") === "scatter" && (
          <ScatterChartView artifacts={artifacts} />
        )}

        {_.get(artifacts, "meta.type") === "pie" && (
          <PieChartView artifacts={artifacts} />
        )}
      </div>
    </div>
  );
});
